import { BillableSourceType, useGetBillableConfig, useUpsertBillableConfig } from 'api/billableConfigs';
import { updateQuestion } from 'redux/actions/QuestionBuilderActions';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';


const useQuestionBillable = ({ questionId, enabled }) => {
  const dispatch = useDispatch();
  const [isQuestionBillable, setIsQuestionBillable] = useState(false);
  const [billableConfigId, setBillableConfigId] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastInitializedQuestionId, setLastInitializedQuestionId] = useState(null);
  const question = useSelector(state => state.questionBuilder.question);

  // Use the question ID from either prop or Redux state
  const activeQuestionId = questionId || question?.ID;

  // Fetch existing billable config
  const {
    data: billableConfig,
    isFetching: isQBFetching,
    isSuccess,
  } = useGetBillableConfig(
    activeQuestionId,
    BillableSourceType.BILLABLE_QUESTION,
    enabled && !!activeQuestionId,
    { showAlerts: true },
  );

  // Setup mutation for creating/updating
  const {
    mutate: upsertConfig,
    isLoading: isQBCreating,
  } = useUpsertBillableConfig({ showAlerts: true });

  // Track if the operation completed
  const [isQBCreated, setIsQBCreated] = useState(false);

  // Update local state when config is fetched
  useEffect(() => {
    // Skip if we've already initialized this question and user has unsaved changes
    if (lastInitializedQuestionId === activeQuestionId && hasUnsavedChanges) {
      return;
    }

    // Skip if no question ID yet (happens during initial mount)
    if (!activeQuestionId) {
      return;
    }

    if (isSuccess && billableConfig) {
      setIsQuestionBillable(billableConfig.value === 'true');
      setBillableConfigId(billableConfig.ID);
      setHasUnsavedChanges(false);
      setLastInitializedQuestionId(activeQuestionId);
    }
    else if (isSuccess && !billableConfig) {
      // No config exists, default to false
      setIsQuestionBillable(false);
      setBillableConfigId(null);
      setHasUnsavedChanges(false);
      setLastInitializedQuestionId(activeQuestionId);
    }
  }, [isSuccess, billableConfig, activeQuestionId, lastInitializedQuestionId, hasUnsavedChanges]);

  // For new questions without an ID, initialize from Redux pendingBillable
  useEffect(() => {
    // Skip if already initialized or if we have a question ID (handled by other effect)
    if (lastInitializedQuestionId || activeQuestionId) {
      return;
    }

    const pendingValue = question.pendingBillable;
    if (pendingValue !== undefined) {
      const boolValue = pendingValue === true || pendingValue === 'true';
      setIsQuestionBillable(boolValue);
    }
    else {
      // New question with no pending value - initialize to false
      setIsQuestionBillable(false);
    }
    setLastInitializedQuestionId('new');
  }, [activeQuestionId, question.pendingBillable, lastInitializedQuestionId]);

  const handleToggleChange = (newValue) => {
    setIsQuestionBillable(newValue);
    setHasUnsavedChanges(true);
    dispatch(updateQuestion({ pendingBillable: newValue }));
  };

  const saveQuestionBillable = () => {
    // If no changes were made or no question ID exists yet, just mark as completed
    if (!hasUnsavedChanges || !activeQuestionId) {
      setIsQBCreated(true);
      return;
    }

    const payload = {
      sourceID: activeQuestionId,
      sourceType: BillableSourceType.BILLABLE_QUESTION,
      value: isQuestionBillable.toString(),
    };

    // Include ID if updating existing config
    if (billableConfigId) {
      payload.ID = billableConfigId;
    }

    upsertConfig(payload, {
      onSuccess: (response) => {
        setIsQBCreated(true);
        setHasUnsavedChanges(false);
        // Update the config ID if this was a create operation
        if (response?.data?.config?.ID) {
          setBillableConfigId(response.data.config.ID);
        }
      },
    });
  };

  const resetQuestionBillable = () => {
    setIsQuestionBillable(false);
    setHasUnsavedChanges(false);
    setIsQBCreated(false);
    dispatch(updateQuestion({ pendingBillable: undefined }));
  };

  return {
    isQBFetching,
    isQBCreating,
    isQBCreated,
    isQuestionBillable,
    setIsQuestionBillable: handleToggleChange,
    saveQuestionBillable,
    resetQuestionBillable,
  };
};

export default useQuestionBillable;
